import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface TipoContaPagarType {
    id: number;
    name: string;    
    empresaId: number;
}

export interface CreateTipoContaPagarType {
    name: string;
    empresaId: number;
}

export interface UpdateTipoContaPagarType {
    id: number;
    name: string;
}

export default function useTipoContaPagar() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listTipoContaPagar, setListTipoContaPagar] = useState<TipoContaPagarType[]>([]);
    const [listTipoContaPagarByEmpresa, setListTipoContaPagarByEmpresa] = useState<TipoContaPagarType[]>([]);
    const [tipoContaPagarPageCount, setTipoContaPagarPageCount] = useState<number>(0);
    const [initialListTipoContaPagar, setInitialListTipoContaPagar] = useState<TipoContaPagarType[]>([]);
    const [tipoContaPagar, setTipoContaPagar] = useState<TipoContaPagarType>({
        id: 0,
        name: "",
        empresaId: 0,
    });

    function createTipoContaPagar(createTipoContaPagarType: CreateTipoContaPagarType): Promise<CreateTipoContaPagarType | null> {
        setLoading(true);
        return api
            .post(`/TipoContaPagar/create`, createTipoContaPagarType)
            .then((response) => {
                const createdTipoContaPagar: CreateTipoContaPagarType = response.data;
                showToast("success", "Categoria financeira criada com sucesso.");
                setLoading(false);
                return createdTipoContaPagar;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar categoria financeira. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar categoria financeira:", error);
                return null;
            });
    }

    function getAllTipoContaPagarPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
    ): Promise<TipoContaPagarType[] | null> {
        setLoading(true);
        let url = `/TipoContaPagar/getAllTipoContaPagarPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListTipoContaPagar(response.data.result);
                setInitialListTipoContaPagar(response.data.result);
                setTipoContaPagarPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar categorias do financeiro. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar categorias do financeiro:", error);
                return null;
            });
    }

    function updateTipoContaPagar(updateTipoContaPagarData: UpdateTipoContaPagarType): Promise<UpdateTipoContaPagarType | null> {
        setLoading(true);
        return api
            .put(`/TipoContaPagar/${updateTipoContaPagarData.id}`, updateTipoContaPagarData)
            .then((response) => {
                showToast("success", "Categoria atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar categoria. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar categoria:", error);
                return null;
            });
    }

    function deleteTipoContaPagar(tipoContaPagarId: number): Promise<TipoContaPagarType | null> {
        setLoading(true);
        return api
            .delete(`/TipoContaPagar/${tipoContaPagarId}`)
            .then(() => {
                setTipoContaPagar({
                    id: 0,
                    name: "",
                    empresaId: 0,
                });
                showToast("success", "Categoria deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar categoria. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar categoria:", error);
                return null;
            });
    }

    function handleGetTipoContaPagarByEmpresaId(empresaId: number): Promise<TipoContaPagarType[] | null> {
            setLoading(true);
            return api
                .get(`/TipoContaPagar/getTipoContaPagarByEmpresaId?empresaId=${empresaId}`)
                .then((response) => {
                    const tipoContaPagar: TipoContaPagarType[] = response.data;
                    setListTipoContaPagarByEmpresa(tipoContaPagar);
                    setLoading(false);
                    return tipoContaPagar;
                })
                .catch((error) => {
                    if (error?.response?.status === 401) {
                        handleLogout();
                        showToast(
                            "error",
                            "Sessão desconectada. Favor login novamente.",
                            error
                        );
                    } else {
                        showToast(
                            "error",
                            "Erro ao buscar informações das categorias do financeiro. Verifique novamente mais tarde.",
                            error
                        );
                    }
                    setLoading(false);
                    console.error("Erro ao buscar informações das categorias do financeiro:", error);
                    return null;
                });
        }

    return {
        createTipoContaPagar,
        getAllTipoContaPagarPaged,
        updateTipoContaPagar,
        deleteTipoContaPagar,
        setListTipoContaPagar,
        listTipoContaPagar,
        setTipoContaPagarPageCount,
        tipoContaPagarPageCount,
        setInitialListTipoContaPagar,
        initialListTipoContaPagar,
        setTipoContaPagar,
        tipoContaPagar,
        handleGetTipoContaPagarByEmpresaId,
        setListTipoContaPagarByEmpresa,
        listTipoContaPagarByEmpresa,
    };
}